import type React from "react";
import {Chip, Divider, Paper, Stack, Typography} from "@mui/material";
import ShieldIcon from '~/icons/svg/ShieldIcon.svg?react'
import StoreIcon from '~/icons/svg/StoreIcon.svg?react'
import {EMPTY_CELL, formatDate} from "~/components/data-grid/cells/gridCellFormat";
import {daysLeftLine} from "~/components/data-grid/cells/gridCells";
import type {
    BuQualification,
    ComplianceArtifact,
    ComplianceRequirement,
} from "~/pages/supplier-360/types/Supplier360Types";
import {
    COMPANY_QUALIFICATION_COLOR,
    COMPANY_QUALIFICATION_LABEL,
    QUALIFICATION_COLOR,
    QUALIFICATION_LABEL,
} from "~/pages/supplier-360/tabs/SupplierCompliance/constants/complianceLabels";
import {STATUS_CHIP} from "~/components/status-chip/statusChips";
import {BU_STATUS_CHIP} from "~/pages/supplier-360/tabs/SupplierCompliance/components/complianceBuStatusChips";
import {VERIFICATION_CHIP} from "~/pages/supplier-360/tabs/SupplierCompliance/components/complianceVerificationChips";
import {REMINDER_CHIP} from "~/pages/supplier-360/tabs/SupplierCompliance/components/complianceReminderChips";
import {requalLine} from "~/pages/supplier-360/tabs/SupplierCompliance/components/complianceCells";

type QualificationRailProps = {
    record: ComplianceRequirement;
    supplierName: string;
    /** The record's BU qualification; undefined for company-wide records. */
    qualification: BuQualification | undefined;
    /** The most recently uploaded evidence — source of Verification, Issuing body, Reference number, Issued. */
    latestArtifact: ComplianceArtifact | undefined;
};

/** One label / value line of the rail — the design's `.sxc-fact`. */
function fact(label: string, value: React.ReactNode) {
    return (
        <Stack  sx={{justifyContent: 'space-between', gap: 0.5}}>
            <Typography variant="caption" fontWeight={600} color="text.secondary">{label}</Typography>
            {typeof value === 'string' ? <Typography variant="caption" fontWeight={600}>{value}</Typography> : value}
        </Stack>
    );
}

/**
 * The record detail page's right rail (`SxcRequirementDetail` → `sx-rail-card`), limited to what
 * the API backs: the record (§2.8), its BU qualification (§2.7) and its latest evidence (§2.9).
 * A company-wide record lists the BUs it satisfies (`applies_to`), hidden while it has none.
 * Scope of approval, approver, review cadence, coverage, audits and the audit-trail export have no
 * backend and are left out.
 */
export const QualificationRail: React.FC<QualificationRailProps> = ({
                                                                        record,
                                                                        supplierName,
                                                                        qualification,
                                                                        latestArtifact,
                                                                    }) => {
    const isCompanyWide = record.layer === 'company_wide';

    return (
        <Paper variant="outlined" sx={{p: 2, borderRadius: 1}}>
            <Stack gap={2}>
                <Stack direction="row" gap={1} sx={{alignItems: 'center'}}>
                    <ShieldIcon width={16} height={16}/>
                    <Typography variant="subtitle2">Qualification</Typography>
                </Stack>

                <Stack gap={0.5}>
                    {isCompanyWide
                        ? (
                            <Chip
                                size="small"
                                color={COMPANY_QUALIFICATION_COLOR[record.state]}
                                label={COMPANY_QUALIFICATION_LABEL[record.state]}
                                sx={{alignSelf: 'flex-start'}}
                            />
                        )
                        : qualification && (
                            <Chip
                                size="small"
                                color={QUALIFICATION_COLOR[qualification.qualification_status]}
                                label={QUALIFICATION_LABEL[qualification.qualification_status]}
                                sx={{alignSelf: 'flex-start'}}
                            />
                        )}
                    <Typography variant="caption" color="text.secondary">
                        {isCompanyWide
                            ? `${supplierName} · company layer`
                            : `${supplierName} × ${qualification?.business_unit?.bu_name ?? EMPTY_CELL}`}
                    </Typography>
                </Stack>

                <Divider/>

                <Stack gap={1.25}>
                    {/* The chips' own labels ("Met", "Pending check") rather than the raw API values. */}
                    {fact('Record state', String(STATUS_CHIP[record.state].props.label))}
                    {fact('Valid until', formatDate(record.expires_at))}
                    {fact('Renewal window', daysLeftLine(record.expires_at, false))}
                    {fact('Verification', latestArtifact ? String(VERIFICATION_CHIP[latestArtifact.verification_status].props.label) : EMPTY_CELL)}
                    {isCompanyWide ? (
                        <>
                            {fact('Issuing body', latestArtifact?.issuing_body || EMPTY_CELL)}
                            {fact('Reference number', latestArtifact?.number || EMPTY_CELL)}
                            {fact('Issued', formatDate(latestArtifact?.issued_at ?? null))}
                        </>
                    ) : (
                        <>
                            {fact('Requalification due', requalLine(qualification?.requalification_due ?? null))}
                            {fact('Risk class', qualification?.risk_class || EMPTY_CELL)}
                        </>
                    )}
                </Stack>
                <Stack gap={1}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Reminders</Typography>
                    <Stack direction="row">{REMINDER_CHIP[record.last_reminder_state]}</Stack>
                </Stack>
                {isCompanyWide && record.applies_to.length > 0 && (
                    <Stack gap={1}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            {`Satisfies in ${record.applies_to.length} business unit${record.applies_to.length === 1 ? '' : 's'}`}
                        </Typography>
                        {record.applies_to.map(bu => (
                            <Stack key={bu.id} direction="row" gap={1} sx={{alignItems: 'center'}}>
                                <StoreIcon width={14} height={14}/>
                                <Typography variant="caption" noWrap sx={{flex: 1, minWidth: 0}}>
                                    {bu.business_unit?.bu_name ?? EMPTY_CELL}
                                </Typography>
                                {BU_STATUS_CHIP[bu.status]}
                            </Stack>
                        ))}
                    </Stack>
                )}
            </Stack>
        </Paper>
    );
};
